import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { ExternalLink, Link2, Unlink } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLinkRfiResponseToProposal, useListRfiResponsesForProposal, useUnlinkRfiResponseFromProposal } from './proposalRfiHooks';
/**
 * P-L3c — linked RFI Response workspaces on a proposal for requirement
 * traceability back to shaping answers.
 */ export function ProposalRfiResponsesPanel({ proposalProjectId }) {
    const { data, isLoading } = useListRfiResponsesForProposal(proposalProjectId);
    const linkMutation = useLinkRfiResponseToProposal();
    const unlinkMutation = useUnlinkRfiResponseFromProposal();
    const [rfiProjectId, setRfiProjectId] = useState('');
    const workspaces = data?.workspaces ?? [];
    const handleLink = ()=>{
        const trimmed = rfiProjectId.trim();
        if (!trimmed) return;
        linkMutation.mutate({
            proposalProjectId,
            rfiResponseProjectId: trimmed
        }, {
            onSuccess: ()=>setRfiProjectId('')
        });
    };
    return /*#__PURE__*/ _jsxs("section", {
        className: "rounded-xl border border-slate-200 bg-white p-4 shadow-sm",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900",
                children: [
                    /*#__PURE__*/ _jsx(Link2, {
                        className: "h-4 w-4 text-slate-500",
                        "aria-hidden": "true"
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        children: "Linked RFI Responses"
                    })
                ]
            }),
            isLoading ? /*#__PURE__*/ _jsx("div", {
                className: "h-12 animate-shimmer rounded-lg bg-slate-100"
            }) : workspaces.length === 0 ? /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-slate-600",
                children: "No RFI Response workspaces linked yet. Link upstream shaping workspaces so compliance can trace requirement understanding."
            }) : /*#__PURE__*/ _jsx("ul", {
                className: "mb-3 space-y-1.5",
                children: workspaces.map((w)=>/*#__PURE__*/ _jsxs("li", {
                        className: "flex items-center justify-between gap-3 rounded-lg border border-slate-100 bg-slate-50/40 px-3 py-2 text-sm",
                        children: [
                            /*#__PURE__*/ _jsxs(Link, {
                                to: `/rfi-response/${w.zouProjectId || w.rfiResponseProjectId}`,
                                className: "flex min-w-0 flex-1 items-center gap-1.5 font-medium text-blue-700 no-underline hover:text-blue-900",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "truncate",
                                        children: w.title
                                    }),
                                    /*#__PURE__*/ _jsx(ExternalLink, {
                                        className: "h-3 w-3 flex-shrink-0",
                                        "aria-hidden": true
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "ghost",
                                size: "sm",
                                iconLeft: /*#__PURE__*/ _jsx(Unlink, {
                                    className: "h-3.5 w-3.5"
                                }),
                                disabled: unlinkMutation.isPending,
                                onClick: ()=>unlinkMutation.mutate({
                                        proposalProjectId,
                                        rfiResponseProjectId: w.rfiResponseProjectId
                                    }),
                                children: "Unlink"
                            })
                        ]
                    }, w.rfiResponseProjectId))
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-end gap-2",
                children: [
                    /*#__PURE__*/ _jsxs("label", {
                        className: "flex min-w-[12rem] flex-1 flex-col gap-1 text-xs text-slate-600",
                        children: [
                            "RFI Response project id",
                            /*#__PURE__*/ _jsx("input", {
                                type: "text",
                                value: rfiProjectId,
                                onChange: (e)=>setRfiProjectId(e.target.value),
                                placeholder: "pangaea or zou project id",
                                className: "rounded-md border border-slate-200 px-2 py-1.5 text-sm text-slate-900"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        disabled: !rfiProjectId.trim() || linkMutation.isPending,
                        onClick: handleLink,
                        children: "Link workspace"
                    })
                ]
            })
        ]
    });
}
